import { useEffect, useMemo, useState } from "react";
import API_ENDPOINTS from "../../shared/api-endpoints";
import { scrollToIslandTop } from "../../shared/forms/scroll";
import useRecaptcha from "../../shared/hooks/useRecaptcha";

function isBlank(value) {
  return String(value || "").trim().length === 0;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function isValidPhone(value) {
  return /^[0-9 ()+-]+$/.test(String(value || "").trim());
}

function parseResponseBody(text) {
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function normaliseServerErrors(body) {
  if (!body) {
    return ["Unexpected error."];
  }

  if (Array.isArray(body)) {
    return body;
  }

  if (typeof body === "string") {
    return [body];
  }

  if (body.message) {
    return [body.message];
  }

  return ["Unexpected error."];
}

function getResponseCode(result) {
  return Number(result?.ResponseCode ?? result?.responseCode ?? 0);
}

function buildFieldErrors(form) {
  const errors = {};

  if (isBlank(form.firstName)) {
    errors.firstName = "required";
  } else if (form.firstName.length > 256) {
    errors.firstName = "maxLength";
  }

  if (isBlank(form.surname)) {
    errors.surname = "required";
  } else if (form.surname.length > 256) {
    errors.surname = "maxLength";
  }

  if (isBlank(form.email)) {
    errors.email = "required";
  } else if (form.email.length > 100) {
    errors.email = "maxLength";
  } else if (!isValidEmail(form.email)) {
    errors.email = "email";
  }

  if (form.phone && form.phone.length > 32) {
    errors.phone = "maxLength";
  } else if (form.phone && !isValidPhone(form.phone)) {
    errors.phone = "phone";
  }

  if (form.mprn && form.mprn.length > 100) {
    errors.mprn = "maxLength";
  }

  if (isBlank(form.desc)) {
    errors.desc = "required";
  } else if (form.desc.length > 4000) {
    errors.desc = "maxLength";
  }

  if (!(form.when instanceof Date) || Number.isNaN(form.when.getTime())) {
    errors.when = "required";
  } else if (form.when > new Date()) {
    errors.when = "maxDate";
  }

  if (isBlank(form.yourIdeal)) {
    errors.yourIdeal = "required";
  } else if (form.yourIdeal.length > 1000) {
    errors.yourIdeal = "maxLength";
  }

  if (!form.tandc) {
    errors.tandc = "required";
  }

  return errors;
}

export default function useComplaintForm(config) {
  const [form, setForm] = useState({
    firstName: "",
    surname: "",
    email: "",
    phone: "",
    address: "",
    mprn: "",
    refNum: "",
    desc: "",
    when: null,
    yourIdeal: "",
    files: [],
    tandc: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [serverSideErrors, setServerSideErrors] = useState([]);
  const [submitResult, setSubmitResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const { getResponse: getRecaptchaResponse, reset: resetRecaptcha } = useRecaptcha({
    sitekey: config.captchaKey,
    onSuccess: () => setRecaptchaError(""),
  });

  const validationItems = useMemo(
    () =>
      [
        fieldErrors.firstName ? config.labels.firstNameLabel : "",
        fieldErrors.surname ? config.labels.surnameLabel : "",
        fieldErrors.email ? config.labels.emailLabel : "",
        fieldErrors.phone ? config.labels.telephoneLabel : "",
        fieldErrors.desc ? config.labels.descriptionLabel : "",
        fieldErrors.when ? config.labels.whenLabel : "",
        fieldErrors.yourIdeal ? config.labels.yourIdealLabel : "",
        fieldErrors.tandc ? config.labels.disclaimerTitle : "",
        recaptchaError ? config.labels.captchaMissingLabel : "",
      ].filter(Boolean),
    [config.labels, fieldErrors, recaptchaError],
  );

  useEffect(() => {
    if (!hasAttemptedSubmit) {
      return;
    }

    setFieldErrors(buildFieldErrors(form));
  }, [form, hasAttemptedSubmit]);

  useEffect(() => {
    const loader = document.querySelector(".loader");
    if (loader) {
      loader.style.display = loading ? "block" : "none";
    }
  }, [loading]);

  function updateField(name, value) {
    setServerSideErrors([]);
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function addUploadedFile(file) {
    setServerSideErrors([]);
    setForm((current) => ({
      ...current,
      files: current.files.concat(file),
    }));
  }

  function removeFileByNameGuid(nameGuid) {
    setForm((current) => ({
      ...current,
      files: current.files.filter((item) => item.nameGuid !== nameGuid),
    }));
  }

  function validateForm() {
    const nextFieldErrors = buildFieldErrors(form);
    const captchaResponse = getRecaptchaResponse();

    setFieldErrors(nextFieldErrors);
    setRecaptchaError(captchaResponse ? "" : "Complete captcha challenge");

    return {
      valid: !Object.keys(nextFieldErrors).length && Boolean(captchaResponse),
      captchaResponse,
    };
  }

  async function submitForm(event) {
    event.preventDefault();
    setHasAttemptedSubmit(true);
    setServerSideErrors([]);

    const { valid, captchaResponse } = validateForm();
    if (!valid) {
      scrollToIslandTop("complaint-form-island");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.complaintSubmit, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          FirstName: form.firstName,
          Surname: form.surname,
          Email: form.email,
          Phone: form.phone,
          OrganisationId: form.organisationId,
          GasSupplyAddress: form.address,
          Mprn: form.mprn,
          PastReference: form.refNum,
          IdealOutcome: form.yourIdeal,
          DateOfOccurence: form.when,
          Description: form.desc,
          Files: form.files,
          CaptchaVerification: captchaResponse,
        }),
      });

      const rawBody = await response.text();
      const body = parseResponseBody(rawBody);

      if (!response.ok) {
        throw { body };
      }

      setSubmitResult(body);
      scrollToIslandTop("complaint-form-island");
    } catch (error) {
      const body = error?.body;

      if (getResponseCode(body)) {
        setSubmitResult(body);
        scrollToIslandTop("complaint-form-island");
      } else {
        setServerSideErrors(normaliseServerErrors(body));
        resetRecaptcha();
        setRecaptchaError("");
        scrollToIslandTop("complaint-form-island");
      }
    } finally {
      setLoading(false);
    }
  }

  return {
    form,
    fieldErrors,
    serverSideErrors,
    submitResult,
    loading,
    recaptchaError,
    validationItems,
    updateField,
    addUploadedFile,
    removeFileByNameGuid,
    submitForm,
    getResponseCode,
  };
}
